/**
 * LayoutMobileFirst — Progressive Enhancement
 *
 * Mobile (<768px): header slim + content full-width + bottom navigation bar
 * Tablet/Desktop (≥768px): sidebar laterale + header + content (identico a MainLayout)
 *
 * CSS-driven: le due strutture coesistono nel DOM, la visibilità
 * è controllata da @media (min-width: 768px) in LayoutMobileFirst.css
 */
import { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Badge,
  theme,
  Typography,
  Button,
  Drawer,
} from 'antd'
import type { MenuProps } from 'antd'
import {
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuOutlined,
  SettingOutlined,
  ApiOutlined,
} from '@ant-design/icons'
import { useAuth } from '@/contexts/AuthContext'
import { usePermission } from '@/hooks/usePermission'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useRegistryMenu, resolveIcon } from '@/hooks/useRegistryMenu'
import './LayoutMobileFirst.css'

const { Header, Sider, Content } = Layout
const { Text } = Typography

// Voci che compaiono nella bottom navigation (max 5).
// Filtrate da flatVisible: /profile è incluso perché ha senzaGateVoluto (sempre visibile).
const BOTTOM_NAV_KEYS = ['/dashboard', '/pre-accettazione', '/schede-lavoro', '/rda', '/profile']

export function LayoutMobileFirst() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { title } = usePageTitle()
  const { token } = theme.useToken()
  // 'menu utente' (profile/settings/integrazioni) escluso dal sidebar:
  // profile compare nel bottom nav via flatVisible; settings/integrazioni
  // restano nel dropdown utente (gestiti più in basso con canManageSettings).
  const { items: menuItems, flatVisible } = useRegistryMenu({
    excludeGruppi: ['menu utente'],
  })
  const canManageSettings = usePermission('system.settings')

  const bottomNavItems = flatVisible
    .filter(e => BOTTOM_NAV_KEYS.includes(e.path))
    .map(e => ({
      key:   e.path,
      icon:  resolveIcon(e.icon),
      label: e.label,
    }))

  const userMenuItems: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: 'Profilo', onClick: () => navigate('/profile') },
    { type: 'divider' },
    ...(canManageSettings ? [
      { key: 'settings', icon: <SettingOutlined />, label: 'Impostazioni', onClick: () => navigate('/settings') },
      { key: 'integrazioni', icon: <ApiOutlined />, label: 'Integrazioni', onClick: () => navigate('/integrazioni') },
    ] : []),
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: logout },
  ]

  return (
    <>
      {/* ── MOBILE layout (<768px) ─────────────────────────────── */}
      <div className="mf-mobile-shell">
        <div
          className="mf-mobile-header"
          style={{
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorder}`,
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            style={{ fontSize: 20 }}
          />
          <Text style={{ fontWeight: 600, fontSize: 16, flex: 1, textAlign: 'center' }}>
            {title || 'Garage'}
          </Text>
          <Badge count={0} showZero={false}>
            <BellOutlined
              style={{ fontSize: 20, cursor: 'pointer' }}
              onClick={() => navigate('/notifications')}
            />
          </Badge>
        </div>

        <div className="mf-mobile-content" style={{ background: token.colorBgLayout }}>
          <div style={{ padding: 16, minHeight: '100%' }}>
            <Outlet />
          </div>
        </div>

        {/* Bottom navigation */}
        <div
          className="mf-bottom-nav"
          style={{
            background: token.colorBgContainer,
            borderTop: `1px solid ${token.colorBorder}`,
          }}
        >
          {bottomNavItems.map((item: any) => {
            const active = location.pathname === item.key
            return (
              <button
                key={item.key}
                className={`mf-bottom-nav-item ${active ? 'active' : ''}`}
                style={{ color: active ? token.colorPrimary : token.colorTextSecondary }}
                onClick={() => navigate(item.key)}
              >
                <span className="mf-bottom-nav-icon">{item.icon}</span>
                <span className="mf-bottom-nav-label">{item.label}</span>
              </button>
            )
          })}
        </div>

        {/* Drawer menu completo su mobile */}
        <Drawer
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src="/Tiber-Garage.png" alt="Garage" style={{ height: 32 }} />
            </div>
          }
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={240}
          styles={{ body: { padding: 0 } }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => { navigate(key); setDrawerOpen(false) }}
            style={{ borderRight: 0, height: '100%' }}
          />
        </Drawer>
      </div>

      {/* ── DESKTOP layout (≥768px) ────────────────────────────── */}
      <Layout className="mf-desktop-shell" style={{ minHeight: '100vh' }}>
        <Sider
          collapsible
          theme="light"
          style={{ borderRight: `1px solid ${token.colorBorder}` }}
        >
          <div
            style={{
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${token.colorBorder}`,
            }}
          >
            <img src="/Tiber-Garage.png" alt="Garage" style={{ height: 48, objectFit: 'contain' }} />
          </div>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            style={{ borderRight: 0 }}
          />
        </Sider>
        <Layout>
          <Header
            style={{
              padding: '0 24px',
              background: token.colorBgContainer,
              borderBottom: `1px solid ${token.colorBorder}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text style={{ fontSize: 18, fontWeight: 600 }}>{title}</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <Badge count={0} showZero={false}>
                <BellOutlined style={{ fontSize: 20, cursor: 'pointer' }} onClick={() => navigate('/notifications')} />
              </Badge>
              {canManageSettings && (
                <SettingOutlined
                  style={{ fontSize: 20, cursor: 'pointer', color: token.colorPrimary }}
                  onClick={() => navigate('/settings')}
                />
              )}
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <Avatar icon={<UserOutlined />} />
                  <Text style={{ marginLeft: 8 }}>{user?.nome} {user?.cognome}</Text>
                </div>
              </Dropdown>
            </div>
          </Header>
          <Content style={{ margin: 24 }}>
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </>
  )
}

export default LayoutMobileFirst
